import Payload from "../interfaces/Payload";

export default class Model {
    static CURRENT_PAYLOAD_VERSION = 1;
    static FIT_RESIZE_MARGIN = 10;
    static MIN_IMAGE_SIZE = 50;

    private payload: Payload;
    private sourceWidth: number;
    private sourceHeight: number;
    private cropWidth: number;
    private cropHeight: number;
    private maxCropWidth: number;

    constructor(
        payload: Payload,
        sourceWidth: number,
        sourceHeight: number,
        cropWidth: number,
        cropHeight: number,
        maxCropWidth?: number
    ) {
        // migrate a shallow copy of payload
        this.payload = this.getMigratedPayload(Object.assign({}, payload));
        this.cropWidth = cropWidth;
        this.cropHeight = cropHeight;
        this.sourceWidth = sourceWidth;
        this.sourceHeight = sourceHeight;
        this.maxCropWidth = maxCropWidth;
    }

    getMigratedPayload(payload: Payload): Payload {
        switch (payload.version) {
            // incremental migration from 1->2, 2->3
            // breaks ommited intentionally
            /*
            case 1:
                // implement migration from version 1 to 2
            case 2:
                // implement migration from version 2 to 3
            */
            case Model.CURRENT_PAYLOAD_VERSION:
                // do nothing, it's the current version
                break;
            default:
                throw new Error(`Payload version ${payload.version} unsupported`);
        }
        return payload;
    }

    setCropPosition(cropLeft: number, cropTop: number) {
        let absZoom = this.getAbsoluteZoom();
        if (cropLeft < 0) {
            cropLeft = 0;
        }
        if (cropTop < 0) {
            cropTop = 0;
        }
        cropLeft = Math.min(cropLeft, this.sourceWidth * absZoom - this.cropWidth);
        cropTop = Math.min(cropTop, this.sourceHeight * absZoom - this.cropHeight);

        let cropRight = this.sourceWidth * absZoom - cropLeft - this.cropWidth;
        let cropBottom = this.sourceHeight * absZoom - cropTop - this.cropHeight;

        // Compare to 1 to cover the floating point approximations
        // Safeguard against Infinity cases, because Infinity is not exportable in JSON
        this.payload.cropXRatio = cropLeft < 1 && cropRight < 1 ? 1 : Math.min(cropLeft / cropRight, Number.MAX_VALUE);
        this.payload.cropYRatio = cropTop < 1 && cropBottom < 1 ? 1 : Math.min(cropTop / cropBottom, Number.MAX_VALUE);
    }

    getCropLeft() {
        let absZoom = this.getAbsoluteZoom();
        let cropSpace = this.sourceWidth * absZoom - this.cropWidth;
        // parenthesis here are important, calculations differs in case
        // of a Infinity like cropXRatio
        return cropSpace * (this.payload.cropXRatio / (1 + this.payload.cropXRatio));
    }

    getCropTop() {
        let absZoom = this.getAbsoluteZoom();
        let cropSpace = this.sourceHeight * absZoom - this.cropHeight;
        // parenthesis here are important, calculations differs in case
        // of a Infinity like cropYRatio
        return cropSpace * (this.payload.cropYRatio / (1 + this.payload.cropYRatio));
    }

    getZoom() {
        return this.payload.zoom;
    }

    setZoom(zoom: number) {
        this.payload.zoom = zoom;
    }

    getCropWidth() {
        return this.cropWidth;
    }

    getCropHeight() {
        return this.cropHeight;
    }

    getSourceWidth() {
        return this.sourceWidth;
    }

    getSourceHeight() {
        return this.sourceHeight;
    }

    getPayload() {
        return this.payload;
    }

    getAbsoluteZoom() {
        return this.payload.zoom * this.cropWidth / this.sourceWidth;
    }

    setAbsoluteZoom(absZoom: number) {
        this.payload.zoom = absZoom / this.cropWidth * this.sourceWidth;
    }

    getFullResolutionSize(maxWidth: number, maxHeight: number) {
        let absZoom = this.getAbsoluteZoom();
        let W = this.getCropWidth() / absZoom;
        let H = this.getCropHeight() / absZoom;
        if (W > maxWidth) {
            H = H * maxWidth / W;
            W = maxWidth;
        }
        if (H > maxHeight) {
            W = W * maxHeight / H;
            H = maxHeight;
        }
        return { width: W, height: H };
    }

    resizeCropWidth(width: number) {
        width = Math.min(this.maxCropWidth, Math.max(width, this.getMinWidth()));
        this.resize(width, this.cropHeight);
    }

    resizeCropHeight(height: number) {
        height = Math.max(height, this.getMinHeight());
        this.resize(this.cropWidth, height);
    }

    resizeCropKeepAspect(width: number, height: number) {
        const aspect = this.cropHeight / this.cropWidth;

        if (aspect <= 1) {
            // width is the base of calculations
            width = Math.max(this.getMinWidth(), Math.min(width, this.maxCropWidth));
            height = width * aspect;
        } else {
            // height is the base of calculations
            height = Math.max(this.getMinHeight(), height);
            width = height / aspect;
            if (width > this.maxCropWidth) {
                width = this.maxCropWidth;
                height = width * aspect;
            }
        }
        // in this case, let the zoom change
        // so that the crop area is resized
        // but will contain the same picture inside
        const keepZoom = false;
        this.resize(width, height, keepZoom);
    }

    resize(width: number, height: number, keepZoom = true) {
        if (width > this.getMinWidth() && height > this.getMinHeight()) {
            let currentAbsZoom = this.getAbsoluteZoom();
            this.cropWidth = width;
            this.cropHeight = height;
            if (keepZoom) {
                this.setAbsoluteZoom(Math.max(
                    // we want to keep current absolute zoom, but only if it is below 1 (if is not upscaled)
                    Math.min(1, currentAbsZoom),
                    // this will ensure we don't set an absolute zoom that
                    // will crop outside of the image
                    this.cropWidth / this.sourceWidth,
                    this.cropHeight / this.sourceHeight
                ));
            }
        }
    }

    isUpscaled() {
        return Number(this.getAbsoluteZoom().toFixed(4)) > 1;
    }

    private getMinWidth() {
        return Model.MIN_IMAGE_SIZE;
    }

    private getMinHeight() {
        return Model.MIN_IMAGE_SIZE;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/image-inline-editor/core/Model.ts
 **/